
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getProductName } from "./ProductUtils";
import { Button as ButtonIcon } from "../ui/button";
import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CreditCardProps {
  transaction: any;
  paymentAmount: string;
  onPaymentAmountChange: (value: string) => void;
  onPayment: () => void;
}

export const CreditCard = ({
  transaction,
  paymentAmount,
  onPaymentAmountChange,
  onPayment
}: CreditCardProps) => {
  const getProductNames = () => {
    if (!transaction.stock_items) {
      return <div className="text-gray-500">Produit non disponible</div>;
    }
    if (Array.isArray(transaction.stock_items)) {
      return transaction.stock_items.map((item: any) => {
        if (!item?.data) {
          return <div key={item?.id || 'unknown'} className="text-gray-500">Produit non disponible</div>;
        }
        const name = getProductName(item.data);
        const quantity = transaction.quantity || item.quantity || 1;
        return <div key={item.id}>
            <div className="font-medium">{name}</div>
            <div className="text-sm text-gray-500">Quantité : {quantity}</div>
          </div>;
      });
    }
    if (!transaction.stock_items?.data) {
      return <div className="text-gray-500">Produit non disponible</div>;
    }
    const quantity = transaction.quantity || transaction.stock_items?.quantity || 1;
    const name = getProductName(transaction.stock_items?.data);
    return <div>
        <div className="font-medium">{name}</div>
        <div className="text-sm text-gray-500">Quantité : {quantity}</div>
      </div>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Date non disponible";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Date non valide";
      }
      return format(date, "PPP", {
        locale: fr
      });
    } catch (error) {
      console.error("Erreur lors du formatage de la date:", error);
      return "Date non valide";
    }
  };

  return <Card className="p-4 py-[11px] px-0">
      <div className="space-y-2">
        <div className="border-b pb-2 mb-2 flex items-center gap-2">
          <div className="text-sm font-medium">Client</div>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger onClick={(e) => e.preventDefault()} asChild>
                <ButtonIcon variant="ghost" size="icon" className="h-6 w-6 p-0 font-bold text-zinc-950 text-xl py-[3px] px-[5px] bg-orange-300 hover:bg-orange-200">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </ButtonIcon>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Pour trouver les informations détaillées du client :</p>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Allez dans l'onglet "Historique"</li>
                  <li>Cherchez la date : {formatDate(transaction.created_at)}</li>
                  <li>Montant : {transaction.total_amount.toLocaleString()} FCFA</li>
                  <li>Les informations du client seront affichées dans la note de la transaction</li>
                </ol>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="text-sm text-gray-600">{transaction.customer_name}</div>
          {transaction.customer_contact && <div className="text-sm text-gray-600">{transaction.customer_contact}</div>}
          {transaction.customer_address && <div className="text-sm text-gray-600">{transaction.customer_address}</div>}
        </div>

        <div className="space-y-2">
          {getProductNames()}
        </div>
        <div className="text-sm text-gray-500">
          Montant total: {transaction.total_amount.toLocaleString()} FCFA
        </div>
        <div className="text-sm text-gray-500">
          Reste à payer: {transaction.remaining_amount.toLocaleString()} FCFA
        </div>
        <div className="text-sm text-gray-500">
          Date de création: {formatDate(transaction.created_at)}
        </div>
        {transaction.payment_due_date && <div className="text-sm font-medium text-orange-600">
            Date d'échéance: {formatDate(transaction.payment_due_date)}
          </div>}
        <div className="mt-4 space-y-2">
          <Input type="number" placeholder="Montant du paiement" value={paymentAmount} onChange={e => onPaymentAmountChange(e.target.value)} />
          <Button onClick={onPayment} className="w-full">
            Payer
          </Button>
        </div>
      </div>
    </Card>;
};
